import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { CheckCircle, Circle, Upload, FileText, CreditCard, Receipt, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stepIcons = {
  import_clients: Upload,
  send_first_proposal: FileText,
  connect_payment: CreditCard,
  send_first_invoice: Receipt,
};

export default function OnboardingChecklist({ onClose }) {
  const [steps, setSteps] = useState([]);
  const [state, setState] = useState({});
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);

  async function loadChecklist() {
    try {
      const response = await fetch(`${API_URL}/api/onboarding/checklist`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSteps(data.steps || []);
        setState(data.state || {});
        setProgress(data.progress || 0);
        
        // Trigger confetti if all complete
        if (data.progress >= 100) {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        }
      }
    } catch (error) {
      console.error('Failed to load checklist:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadChecklist();
  }, []);

  async function completeStep(stepKey) {
    try {
      const response = await fetch(`${API_URL}/api/onboarding/complete/${stepKey}`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Step completed!');
        loadChecklist();
      }
    } catch (error) {
      toast.error('Failed to complete step');
    }
  }

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-slate-700 rounded w-1/3"></div>
            <div className="h-2 bg-slate-700 rounded"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-12 bg-slate-700 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const allComplete = progress >= 100;

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="onboarding-checklist">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            {allComplete ? (
              <>
                <PartyPopper className="w-5 h-5 text-yellow-400" />
                Setup Complete!
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 text-indigo-400" />
                Get Started
              </>
            )}
          </CardTitle>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400">
              ×
            </Button>
          )}
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2 bg-slate-700" />
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {steps.map((step) => {
          const Icon = stepIcons[step.key] || Circle;
          const isComplete = state[step.key];
          
          return (
            <div
              key={step.key}
              className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                isComplete
                  ? 'bg-green-500/10 border border-green-500/20'
                  : 'bg-slate-900/50 hover:bg-slate-900/70'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  isComplete ? 'bg-green-500/20' : 'bg-slate-700/50'
                }`}>
                  {isComplete ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : (
                    <Icon className="w-4 h-4 text-slate-400" />
                  )}
                </div>
                <span className={isComplete ? 'text-green-300' : 'text-white'}>
                  {step.label}
                </span>
              </div>
              {!isComplete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => completeStep(step.key)}
                  className="text-indigo-400 hover:text-indigo-300"
                >
                  Mark Done
                </Button>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
